import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "single-shop"
}
const _hoisted_2 = { class: "opening-hours" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = {
  key: 2,
  class: "multi-shop"
}
const _hoisted_8 = { class: "form-group-search" }
const _hoisted_9 = {
  class: "sr-only",
  for: "shop-search"
}
const _hoisted_10 = ["placeholder"]
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", {
  class: "search-icon",
  "aria-hidden": "true"
}, null, -1)
const _hoisted_12 = {
  key: 0,
  class: "shop-list-container"
}
const _hoisted_13 = { class: "shop-list" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "shop-list-name" }
const _hoisted_16 = {
  key: 0,
  class: "contact-information"
}
const _hoisted_17 = { class: "zip-city" }
const _hoisted_18 = { class: "shop-list-info" }
const _hoisted_19 = {
  key: 1,
  class: "shop-list-info__distance"
}
const _hoisted_20 = { key: 0 }
const _hoisted_21 = { key: 1 }
const _hoisted_22 = { key: 2 }
const _hoisted_23 = { key: 3 }
const _hoisted_24 = {
  key: 4,
  class: "badge badge--warning"
}
const _hoisted_25 = {
  key: 0,
  class: "show-more"
}
const _hoisted_26 = { key: 0 }

import {ApiStatus, Shop, OpeningHour, Article} from '@/types';
import {computed, onBeforeMount, onBeforeUnmount, reactive, watch} from 'vue';
import LoaderComponent from '@/components/LoaderComponent.vue';
import {
  formatDistance,
  calculateDistance,
} from '@/functions/position-distance';
import {useI18n} from 'vue-i18n';
import {ErrorMessageStore} from '@/stores/errorMessage';
import {isOpen} from "@/functions";
import FavoriteButton from "@/components/FavoriteButton.vue";
import {AuthStore} from "@/stores/auth";
import axios from "axios";
import {DateTime} from "luxon";

interface State {
  status: ApiStatus;
  shops: Shop[];
  filterString: string;
  visibleShops: number;
  dayOfWeek: string;
  loadingText: string;
}

/* Const */

export default /*#__PURE__*/_defineComponent({
  __name: 'ShopSelector',
  emits: ['selectShop'],
  setup(__props, { emit }) {

/* imports */
const authStore = AuthStore();

const errorMessageStore = ErrorMessageStore();

const {t, locale} = useI18n();

/* interface */
const pageSize = 6;

/* Reactive */
const state = reactive<State>({
  status: {
    done: false,
    failed: false,
    loading: false,
  },
  shops: [],
  filterString: '',
  visibleShops: pageSize,
  dayOfWeek: '',
  loadingText: 'loader.text',
});

const cmp = (a:number, b:number) =>{
  if (a > b) return +1;
  if (a < b) return -1;
  return 0;
}

/* Computed */
const filteredShops = computed(() =>
    state.shops.filter(shop =>
        shop.name.toLowerCase().includes(state.filterString.trim().toLowerCase()) ||
        shop.description.toLowerCase().includes(state.filterString.trim().toLowerCase()) ||
        shop.contactInformation.address1.toLowerCase().includes(state.filterString.trim().toLowerCase()) ||
        shop.contactInformation.city.toLowerCase().includes(state.filterString.trim().toLowerCase())
    ).sort((a, b) => {
      return cmp(isFavorite(a) ? 0 : 1,isFavorite(b) ? 0 : 1) || cmp(a.distance, b.distance)
    })
);

const visibleShops = computed(() =>
    filteredShops.value.slice(0, state.visibleShops)
);

const emptyList = computed(() => filteredShops.value.length === 0);

/* Emits */


/* AbortController */
const abortController = new AbortController();

/* Functions */
const isFavorite = (shop: Shop) : boolean => {
  if (shop && authStore.state.isAuthenticated) {
    return authStore.state.user?.favorites?.filter(fav => fav.code == shop.id?.toString())?.length ?? 0 > 0;
  }
  return false;
};

const openingHours = (data: OpeningHour[]): string => {
  const today = data.find(x => x.dayOfWeek.toLowerCase() === state.dayOfWeek.toLowerCase());

  if (today) {
    const openDate =  DateTime.fromISO(today.opens);
    const closeDate = DateTime.fromISO(today.closes);

    return `${openDate.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${closeDate.toLocaleString(DateTime.TIME_24_SIMPLE)}`
  }

  return '';
};

const selectShop = (e:MouseEvent, shop: Shop) => {
  if (shop.externalAppUri) {
    //window.location.href = shop.externalAppUri;
    //window.open(shop.externalAppUri, '_blank')
    return;
  }

  const target = e.target as HTMLElement
  if(target.className.indexOf('btn-favorite') != -1){
    return;
  }

  emit('selectShop', shop);
};

const load = () => {
  state.status.loading = true;
  axios.get('/api/shops', {
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + authStore.state.token?.accessToken,
    }
  })
  .then(response => response.data as Shop[])
  .then((shops: Shop[]) => {
    state.shops = shops;
    calculateShopDistances();
    state.status.done = true;
  })
  .catch(err => {
    state.status.failed = true;
    state.status.text = err.message;
  })
  .finally(async () => {
    if (singleShop.value) {
      state.status.loading = false;
      //No geo-location for single-shops scenarios, exit early
      return;
    }

    if ('permissions' in navigator) {
      const geolocation = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (geolocation.state !== 'granted') {
        state.status.loading = false;
      }
    } else {
      state.status.loading = false;
    }
  });
};

const calculateShopDistances = () => {
  if(singleShop.value) {
    return;
  }
  
  navigator.geolocation.getCurrentPosition(pos => {
    state.loadingText = 'loader.calculatesDistances';
    state.status.loading = true;
    state.shops.forEach((shop: Shop) => {
      const dist = calculateDistance(
          pos.coords.latitude,
          pos.coords.longitude,
          shop.location.latitude,
          shop.location.longitude,
          'K'
      );

      shop.distance = dist;
    });

    setTimeout(() => state.status.loading = false, 500);
  });
};

const singleShop = computed<boolean>(() => {
  return state.shops.length === 1;
});



const showMore = () => (state.visibleShops += pageSize);

/* Watch */
watch(state.status, () => {
  if (state.status.failed && state.status.text) {
    errorMessageStore.show(state.status.text);
  }
});

/* Lifecycle Hooks */
onBeforeMount(() => {
  //defaultLanguage().then(() => {
    load();
  //});

  const date = new Date();
  state.dayOfWeek = date.toLocaleDateString('en', {weekday: 'long'});
});

onBeforeUnmount(() => abortController.abort());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (state.status.loading)
      ? (_openBlock(), _createBlock(LoaderComponent, {
          key: 0,
          text: state.loadingText
        }, null, 8, ["text"]))
      : _createCommentVNode("", true),
    (state.status.done && state.shops && !state.status.loading && _unref(singleShop))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('shopSelector.singleShop.openingHoursHeader')), 1),
            _createElementVNode("span", null, _toDisplayString(openingHours(state.shops[0].openingHours)), 1)
          ]),
          _createElementVNode("button", {
            class: "btn btn-primary",
            disabled: !_unref(isOpen)(state.shops[0]),
            onClick: _cache[0] || (_cache[0] = _withModifiers((e)=>selectShop(e,state.shops[0]), ["prevent"]))
          }, [
            (!_unref(isOpen)(state.shops[0]) && state.shops[0].status === 'paused')
              ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(t)('shopSelector.singleShop.paused')), 1))
              : _createCommentVNode("", true),
            (!_unref(isOpen)(state.shops[0]) && state.shops[0].status !== 'paused')
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(t)('shopSelector.singleShop.closed')), 1))
              : _createCommentVNode("", true),
            (_unref(isOpen)(state.shops[0]))
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(t)('shopSelector.singleShop.start')), 1))
              : _createCommentVNode("", true)
          ], 8, _hoisted_3)
        ]))
      : _createCommentVNode("", true),
    (state.status.done && state.shops && !state.status.loading && !_unref(singleShop))
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("h2", null, _toDisplayString(_unref(t)("home.subHeader")), 1),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, _toDisplayString(_unref(t)('search.label')), 1),
            _withDirectives(_createElementVNode("input", {
              type: "search",
              placeholder: _unref(t)('search.placeholder'),
              class: "search",
              id: "shop-search",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.filterString) = $event))
            }, null, 8, _hoisted_10), [
              [_vModelText, state.filterString]
            ]),
            _hoisted_11
          ]),
          (!_unref(emptyList))
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("ul", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(visibleShops), (shop) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: shop.id
                    }, [
                      _createElementVNode("div", {
                        onClick: _withModifiers((e)=>selectShop(e,shop), ["prevent"]),
                        class: _normalizeClass({'shop-list-item': true, disabled: !_unref(isOpen)(shop) || shop.externalAppUri != null})
                      }, [
                        _createElementVNode("div", _hoisted_15, [
                          _createElementVNode("h2", null, _toDisplayString(shop.description), 1),
                          (shop.contactInformation)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _createElementVNode("div", null, _toDisplayString(shop.contactInformation.address1), 1),
                                _createElementVNode("div", null, _toDisplayString(shop.contactInformation.address2), 1),
                                _createElementVNode("div", _hoisted_17, _toDisplayString(shop.contactInformation.zipCode) + " " + _toDisplayString(shop.contactInformation.city), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", _hoisted_18, [
                          (_unref(authStore).state.isAuthenticated)
                            ? (_openBlock(), _createBlock(FavoriteButton, {
                                key: 0,
                                code: shop.id?.toString() ?? '',
                                type: "shop",
                                favorite: isFavorite(shop)
                              }, null, 8, ["code", "favorite"]))
                            : _createCommentVNode("", true),
                          (shop.distance)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(_unref(formatDistance)(shop.distance)), 1))
                            : _createCommentVNode("", true),
                          (!shop.externalAppUri)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 2,
                                class: _normalizeClass(["badge", [_unref(isOpen)(shop) ? 'badge--success' : 'badge--danger',]])
                              }, [
                                (_unref(isOpen)(shop))
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_20, _toDisplayString(_unref(t)('button.open')), 1))
                                  : _createCommentVNode("", true),
                                (!_unref(isOpen)(shop) && shop.status === 'paused')
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(_unref(t)('button.paused')), 1))
                                  : _createCommentVNode("", true),
                                (!_unref(isOpen)(shop) && shop.status !== 'paused')
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(_unref(t)('button.closed')), 1))
                                  : _createCommentVNode("", true)
                              ], 2))
                            : _createCommentVNode("", true),
                          (!shop.externalAppUri)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(openingHours(shop.openingHours)), 1))
                            : _createCommentVNode("", true),
                          (shop.externalAppUri)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_24, " Kommer snart "))
                            : _createCommentVNode("", true)
                        ])
                      ], 10, _hoisted_14)
                    ]))
                  }), 128))
                ]),
                (state.visibleShops < _unref(filteredShops).length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _createElementVNode("button", {
                        class: "btn btn-primary",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (showMore()))
                      }, _toDisplayString(_unref(t)('button.showMoreRestaurants')), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", null, [
            (_unref(emptyList))
              ? (_openBlock(), _createElementBlock("p", _hoisted_26, _toDisplayString(_unref(t)('search.noMatch')), 1))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})